// src/features/leases/components/DecisionsAheadCallout.jsx
//
// Thin row surfacing the two most urgent forward-looking deadlines.
// Caller passes a list of {label, date, daysToExpiry, description} entries; we
// sort by daysToExpiry ascending and take the top two. Renders
// nothing if there are no items with a future date.
//
// Variants:
//   - "cards" (new canonical): compact row of cards, each showing a category
//     label + countdown dot. No left color bar, no chevron/popover.
//     Used on the Overview tab.
//   - "card" (legacy): freestanding yellow callout. Kept for backwards
//     compatibility.
//   - "inline": width-constrained compact row with per-row expand popover.

import React, { useState, useRef, useEffect } from "react";
import { T } from "../../../app/tokens.js";
import { DaysRemainingPill } from "./DaysRemainingPill.jsx";
import { HeadingTooltip } from "../../../components/HeadingTooltip.jsx";
import { parseDateOnly } from "../leaseTabConfig.js";

function fmtDate(d) {
  if (!d) return "—";
  const dt = parseDateOnly(d);
  if (!dt) return String(d);
  return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

const CARD_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: 18,
  padding: "10px 14px",
  background: T.card,
  border: `1px solid ${T.border}`,
  borderRadius: 8,
};

const LABEL_CARD = {
  fontSize: T.fsXs,
  fontWeight: T.fwBold,
  color: T.amberDark,
  textTransform: "uppercase",
  letterSpacing: 0.5,
};

const LABEL_INLINE = {
  fontSize: T.fsXxs,
  fontWeight: T.fwSemibold,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: T.light,
  flexShrink: 0,
  marginBottom: 4,
};

const POPOVER_STYLE = {
  background: T.tooltip.bg,
  color: T.tooltip.fg,
  borderRadius: 6,
  padding: "10px 14px",
  maxWidth: 320,
  fontSize: T.fsXs,
  boxShadow: T.tooltip.shadow,
  zIndex: 9999,
};

export function DecisionsAheadCallout({ items = [], variant = "card", loading = false }) {
  const [openIdx, setOpenIdx] = useState(null);
  const rowTriggerRefs = useRef([{ current: null }, { current: null }]);

  const allSorted = items
    .filter((i) => i && i.daysToExpiry != null)
    .sort((a, b) => a.daysToExpiry - b.daysToExpiry);

  const ranked = allSorted.slice(0, 2);

  useEffect(() => {
    if (openIdx === null) return;
    function handleOutsideClick(e) {
      const triggerEl = rowTriggerRefs.current[openIdx]?.current;
      if (triggerEl && triggerEl.contains(e.target)) return;
      setOpenIdx(null);
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [openIdx]);

  if (loading) return null;

  if (allSorted.length === 0 || ranked.length === 0) {
    const isInlineEmpty = variant === "inline" || variant === "cards" || variant === "list";
    return (
      <div
        data-testid="decisions-ahead-empty"
        style={isInlineEmpty ? {
          borderTop: `0.5px solid ${T.warmBorder}`,
          paddingTop: 9,
          maxWidth: 680,
          display: "flex",
          alignItems: "center",
        } : {
          ...CARD_STYLE,
          justifyContent: "center",
        }}
      >
        <span style={{ fontSize: T.fsXs, color: T.light, fontStyle: "italic" }}>
          No upcoming key dates recorded yet - add one in the Critical Dates section below.
        </span>
      </div>
    );
  }

  // ── "list" variant (LEASE-DETAIL-V5-S2 Overview 2×2 bottom-right) ───────────
  // Shows ALL items sorted ascending by daysToExpiry (not capped at 2).
  // Each row: label · formatted date · description · DaysRemainingPill.
  // Scrollable when list is long.
  if (variant === "list") {
    return (
      <div
        data-testid="decisions-ahead-list"
        data-variant="list"
        style={{
          background: T.card,
          borderRadius: 6,
          padding: "11px 13px",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <div style={{
          fontSize: T.fsSm,
          fontWeight: T.fwBold,
          color: T.dark,
          marginBottom: 8,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}>
          <span style={{
            display: "inline-block",
            width: 3,
            height: 14,
            background: T.blue,
            borderRadius: 2,
            flexShrink: 0,
          }} />
          Decisions ahead
        </div>
        <div style={{ overflowY: "auto", maxHeight: 280 }}>
          {allSorted.map((it, i) => (
            <div
              key={i}
              data-testid={`decision-list-row-${i}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "5px 0",
                borderBottom: i < allSorted.length - 1 ? `1px solid ${T.warmBorder}` : "none",
              }}
            >
              <span style={{
                flex: 1,
                minWidth: 0,
                fontSize: T.fsSm,
                fontWeight: T.fwSemibold,
                color: T.dark,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                {it.label}
              </span>
              <span style={{
                fontSize: T.fsXs,
                color: T.mid,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}>
                {fmtDate(it.date)}
              </span>
              {it.description && (
                <span style={{
                  fontSize: T.fsXs,
                  color: T.light,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 120,
                  flexShrink: 1,
                }}>
                  {it.description}
                </span>
              )}
              <DaysRemainingPill days={it.daysToExpiry} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── "cards" variant (new canonical for Overview tab) ──────────────────────
  // Compact row of cards: category label (small caps) + countdown with dot.
  // No left color bar, no chevron, no popover.
  if (variant === "cards") {
    return (
      <div
        data-testid="decisions-ahead-callout"
        data-variant="cards"
        style={{
          borderTop: `0.5px solid ${T.warmBorder}`,
          paddingTop: 9,
          maxWidth: 680,
        }}
      >
        <div style={{ ...LABEL_INLINE, marginBottom: 6 }}>Decisions ahead</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {ranked.map((it, i) => {
            const isNearTerm = it.daysToExpiry != null && it.daysToExpiry <= 90;
            return (
              <div
                key={i}
                data-testid={`decision-ahead-${i}`}
                style={{
                  background: T.card,
                  border: `1px solid ${T.border}`,
                  borderRadius: 6,
                  padding: "8px 12px",
                  minWidth: 100,
                }}
              >
                <div style={{
                  fontSize: T.fsXxs,
                  fontWeight: T.fwSemibold,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  color: T.light,
                  marginBottom: 5,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                  {it.label}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{
                    display: "inline-block",
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: isNearTerm ? T.amberDark : T.light,
                    flexShrink: 0,
                  }} />
                  <span style={{
                    fontSize: T.fsBase,
                    fontWeight: T.fwSemibold,
                    color: isNearTerm ? T.amberDark : T.dark,
                  }}>
                    {it.daysToExpiry != null
                      ? `${it.daysToExpiry.toLocaleString()}d`
                      : "—"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const isInline = variant === "inline";

  if (isInline) {
    return (
      <div
        data-testid="decisions-ahead-callout"
        data-variant={variant}
        style={{
          borderTop: `0.5px solid ${T.warmBorder}`,
          paddingTop: 9,
          maxWidth: 680,
        }}
      >
        <div style={LABEL_INLINE}>Decisions ahead</div>
        {ranked.map((it, i) => {
          const isOpen = openIdx === i;
          return (
            <div
              key={i}
              data-testid={`decision-row-${i}`}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "4px 0",
                overflow: "hidden",
                gap: 6,
              }}
            >
              <span
                data-testid={`decision-ahead-${i}`}
                style={{
                  flex: 1,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  fontSize: T.fsXs,
                  color: T.dark,
                }}
              >
                {it.label}
              </span>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginLeft: "auto",
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: T.fsXs, color: T.mid, whiteSpace: "nowrap" }}>
                  {fmtDate(it.date)}
                </span>
                <DaysRemainingPill days={it.daysToExpiry} />
                <button
                  ref={(el) => { rowTriggerRefs.current[i] = rowTriggerRefs.current[i] || { current: null }; rowTriggerRefs.current[i].current = el; }}
                  data-testid={`decision-chevron-${i}`}
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  aria-label="Show details"
                  aria-expanded={isOpen}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "0 2px",
                    color: T.light,
                    fontSize: T.fsSm,
                    lineHeight: 1,
                    transform: isOpen ? "rotate(180deg)" : "none",
                    transition: "transform 0.15s",
                  }}
                >
                  ▾
                </button>
              </div>
              {isOpen && (
                <HeadingTooltip
                  triggerRef={rowTriggerRefs.current[i]}
                  data-testid={`decision-popover-${i}`}
                  style={POPOVER_STYLE}
                >
                  <p style={{ margin: 0, fontSize: T.fsXs, color: T.mid, lineHeight: 1.5 }}>
                    {it.description || "No additional detail recorded."}
                  </p>
                </HeadingTooltip>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // card variant (legacy — kept structurally intact)
  return (
    <div
      data-testid="decisions-ahead-callout"
      data-variant={variant}
      style={CARD_STYLE}
    >
      <span style={LABEL_CARD}>
        Decisions ahead:
      </span>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
        {ranked.map((it, i) => (
          <div key={i} data-testid={`decision-ahead-${i}`} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: T.fsSm, color: T.dark, fontWeight: T.fwSemibold }}>
              {it.label}
            </span>
            <span style={{ fontSize: T.fsSm, color: T.mid, fontWeight: T.fwRegular }}>
              {fmtDate(it.date)}
            </span>
            <DaysRemainingPill days={it.daysToExpiry} />
          </div>
        ))}
      </div>
    </div>
  );
}
